"use client";

import type { BannerWidgetProps } from "@fluid-app/react-widgets-types";
import { AspectRatio, Button, Card, CardContent, cn } from "@fluid-app/ui";

/**
 * E-Commerce Banner Widget Component
 *
 * A flexible, customizable banner for promoting products, offers, or announcements.
 * Integrates with FairShare tracking for analytics.
 */
export function BannerWidget({
  title,
  description,
  imageUrl,
  imageAlt,
  position = "center",
  variant = "default",
  size = "md",
  targetUrl,
  buttonText = "Shop Now",
  showButton = true,
  className,
  aspectRatio = 16 / 9,
  onClick,
  hideWidget = false,
}: BannerWidgetProps) {
  // If hideWidget is true, don't render the component
  if (hideWidget) {
    return null;
  }

  // Handle banner click with tracking
  const handleBannerClick = () => {
    // Execute custom onClick handler if provided
    if (onClick) {
      onClick();
    }
  };

  // Map size to appropriate width classes
  const sizeClasses = {
    sm: "max-w-md",
    md: "max-w-2xl",
    lg: "max-w-4xl",
    full: "w-full",
  };

  // Map position to appropriate placement classes
  const positionClasses = {
    top: "mx-auto mb-8",
    bottom: "mx-auto mt-8",
    left: "mr-auto",
    right: "ml-auto",
    center: "mx-auto",
  };

  // Map variant to appropriate style classes
  const variantClasses = {
    default: "bg-card border shadow-sm",
    outline: "bg-background border-2 border-primary",
    vibrant: "bg-primary/10 border border-primary/20 shadow-md",
    minimal: "bg-transparent",
  };

  return (
    <Card
      className={cn(
        sizeClasses[size],
        positionClasses[position],
        variantClasses[variant],
        "overflow-hidden",
        className,
      )}
    >
      <a
        href={targetUrl}
        onClick={handleBannerClick}
        className="block focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
      >
        <CardContent className="p-0">
          <div className="relative">
            <AspectRatio ratio={aspectRatio}>
              <div className="absolute inset-0">
                <img
                  src={imageUrl}
                  alt={imageAlt}
                  className="h-full w-full object-cover transition-transform duration-300 hover:scale-105"
                  loading="eager"
                />
              </div>
              <div className="absolute inset-0 flex flex-col justify-end bg-gradient-to-t from-black/60 to-transparent p-6 text-white">
                <h3 className="mb-2 text-xl font-bold md:text-2xl">{title}</h3>
                {description && (
                  <p className="mb-4 max-w-md text-sm md:text-base">
                    {description}
                  </p>
                )}
                {showButton && (
                  <div onClick={(e) => e.stopPropagation()} className="z-10">
                    <Button
                      variant="link"
                      size="sm"
                      className="font-medium hover:bg-white/90"
                      onClick={handleBannerClick}
                    >
                      {buttonText}
                    </Button>
                  </div>
                )}
              </div>
            </AspectRatio>
          </div>
        </CardContent>
      </a>
    </Card>
  );
}
